import './ImageUploadForm.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCloud, faExclamationTriangle, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { FileRejection } from 'react-dropzone/.';
import { S_Alert } from '../../../../design/alert/S_Alert';
import { ButtonWithLoader } from '../../../../design/buttons/ButtonWithLoader';
import { S_Card } from '../../../../design/card/card/S_Cards';
import { S_ErrorFeedback } from '../../../../design/feedback/errorFeedback/S_ErrorFeedback';
import { S_WarningFeedback } from '../../../../design/feedback/warningFeedback/S_WarningFeedback';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import { Loader } from '../../../components/Loader';
import { EnableFor } from '../../../containers/EnableForRole';
import { Plan } from '../../user/user.store';
import { useDatasetPermissions } from '../datasets.permissions';
import { DatasetStatus } from '../datasetStatus.model';
import { IImageFile } from '../imagesUpload.service';
import { IRejectedFile } from '../models/IRejectedFile';
import { ImageUploader } from './ImagesUploader';

library.add(faUpload, faCloud, faExclamationTriangle);

export interface IImageUploadProps {
  datasetId: string;
  datasetStatus: DatasetStatus;
  isCreationEnabled: boolean;
  isCancelEnabled: boolean;
  isEncryptionEnabled: boolean;
  isProcessingImages: boolean;
  imagesToUploadFromFile: IImageFile[];
  imagesRejectedFromFile: IRejectedFile[];
  willExceedsUploadImageSizeLimit: boolean;
  imagePreValidationWasSuccessful: boolean;
  onFormSubmit: () => Promise<void>;
  cleanImagesFromFile: () => void;
  addImagesToDataset: (images: File[], fileRejections: FileRejection[]) => Promise<void>;
  toggleUploadConfirmationModal: () => void;
  isConfirmationModalOpen: boolean;
  ownerPlan: Plan;
  datasetCreatorId: string;
  loaderKey?: string;
  currentFilesSizeInB: number;
}

interface IImageUploadFormProps {
  onCancel: () => void;
}

export const ImageUploadForm = observer((props: IImageUploadProps & IImageUploadFormProps): ReactElement => {
  const { t } = useTranslator();
  const permissions = useDatasetPermissions();
  const fileSizeSumInB = props.currentFilesSizeInB + props.imagesToUploadFromFile.map((x) => x.file.size).reduce((a, b) => a + b, 0);
  const isToBig = fileSizeSumInB > 1024 * 1024 * 1024; // 1 GB

  return (
    <>
      <S_Card className='dataset-upload-card'>
        <Loader isLoading={props.datasetStatus !== 'Draft' && props.datasetStatus !== 'Published'}>
          <S_Alert color='info'>{t('exifs_info')}</S_Alert>
          {isToBig && (
            <S_Alert color='warning'>
              {props.currentFilesSizeInB > 0 ? t('dataset_size_limit_dataset_update') : t('dataset_size_limit_dataset_creation')}
            </S_Alert>
          )}
          <div className='permissions-overlay' />
          <div className='strong'>{t('please_select_your_files')}</div>
          <ImageUploader
            datasetId={props.datasetId}
            isProcessingImages={props.isProcessingImages}
            imagesToUploadFromFile={props.imagesToUploadFromFile}
            imagesRejectedFromFile={props.imagesRejectedFromFile}
            addImagesToDataset={props.addImagesToDataset}
          />
          {props.willExceedsUploadImageSizeLimit && (
            <S_ErrorFeedback>
              {t('uploading_selected_file_will_exceed_workspace_owners_total_files_size_limit', {
                plan: t(props.ownerPlan),
              })}
            </S_ErrorFeedback>
          )}
          {!props.imagePreValidationWasSuccessful && (
            <S_WarningFeedback>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className='warning-icon'
              />{' '}
              {t('we_were_not_able_to_validate_all_provided_files')}
            </S_WarningFeedback>
          )}
          <div className='dataset-upload-card-buttons'>
            <Button
              color='neutral'
              variant='outlined'
              onClick={props.onCancel}
              id='cancel-button'
              disabled={!props.isCancelEnabled}
            >
              {t('cancel')}
            </Button>
            <EnableFor hasPermission={permissions.canUploadImages(props.datasetStatus, props.datasetCreatorId)}>
              <ButtonWithLoader
                color={'primary'}
                disabled={!props.isCreationEnabled}
                loaderKey={props.loaderKey}
                onClick={props.datasetStatus === 'Draft' ? props.onFormSubmit : props.toggleUploadConfirmationModal}
                id='upload-button'
              >
                {t('upload')}
              </ButtonWithLoader>
            </EnableFor>
          </div>
        </Loader>
      </S_Card>
      <ConfirmationModal
        confirmationHeader='confirm_image_upload'
        confirmationQuestion='confirm_image_upload_body'
        confirmationYes={'continue'}
        onCancel={props.toggleUploadConfirmationModal}
        onConfirm={props.onFormSubmit}
        showModal={props.isConfirmationModalOpen}
      />
    </>
  );
});
